import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTransactions, createTransaction, updateTransaction, deleteTransaction, getProperties, getUnits } from '../services/api';
import { CurrencyEur, Plus, Trash, PencilSimple, TrendUp, TrendDown, X, FunnelSimple } from '@phosphor-icons/react';

const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export default function Transactions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();

  const [transactions, setTransactions] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // collected across an unfiltered fetch on mount
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [filter, setFilter] = useState({
    type: searchParams.get('type') || '',
    property_id: searchParams.get('property_id') || '',
    category: searchParams.get('category') || '',
    year: searchParams.get('year') ? parseInt(searchParams.get('year')) : 0, // 0 = all years
    month: searchParams.get('month') ? parseInt(searchParams.get('month')) : 0, // 0 = all months
  });

  // Sync URL when filter changes (without re-creating history entries)
  useEffect(() => {
    const p = new URLSearchParams();
    if (filter.type) p.set('type', filter.type);
    if (filter.property_id) p.set('property_id', filter.property_id);
    if (filter.category) p.set('category', filter.category);
    if (filter.year) p.set('year', String(filter.year));
    if (filter.month) p.set('month', String(filter.month));
    setSearchParams(p, { replace: true });
  }, [filter, setSearchParams]);

  const loadData = async () => {
    try {
      const params = {};
      if (filter.type) params.type = filter.type;
      if (filter.property_id) params.property_id = filter.property_id;
      if (filter.category) params.category = filter.category;
      if (filter.year) params.year = filter.year;
      if (filter.month) params.month = filter.month;

      const [transactionsRes, propertiesRes, unitsRes] = await Promise.all([
        getTransactions(params),
        getProperties(),
        getUnits()
      ]);
      setTransactions(transactionsRes.data);
      setProperties(propertiesRes.data);
      setUnits(unitsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filter]);

  // Load all categories once on mount (unfiltered) — used to populate the filter dropdown
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await getTransactions({});
        const cats = Array.from(new Set(res.data.map(t => t.category).filter(Boolean))).sort();
        setAllCategories(cats);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, []);

  // Categories that match the current type filter (income vs expense), to keep the
  // dropdown focused; if type is empty, show all.
  const filteredCategoryOptions = useMemo(() => {
    if (!filter.type) return allCategories;
    // Find the categories present in transactions that match the type filter
    const byType = new Set(
      transactions.filter(t => t.type === filter.type).map(t => t.category).filter(Boolean)
    );
    // Add the currently selected category to keep it visible even if no rows match yet
    if (filter.category) byType.add(filter.category);
    return Array.from(byType).sort();
  }, [allCategories, transactions, filter.type, filter.category]);

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diese Transaktion wirklich löschen?')) return;
    try {
      await deleteTransaction(id);
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      rent: 'Miete',
      deposit: 'Kaution',
      utility: 'Nebenkosten',
      maintenance: 'Wartung',
      repair: 'Reparatur',
      insurance: 'Versicherung',
      tax: 'Steuern',
      management: 'Verwaltung',
      other: 'Sonstige'
    };
    return labels[category] || category;
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop?.name || '—';
  };

  // Calculate totals
  const totals = transactions.reduce((acc, t) => {
    if (t.type === 'income') {
      acc.income += t.amount;
    } else {
      acc.expense += t.amount;
    }
    return acc;
  }, { income: 0, expense: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transactions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            Transaktionen
          </h1>
          <p className="text-slate-500 mt-0.5 text-sm">Einnahmen und Ausgaben verfolgen</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors inline-flex items-center justify-center gap-2 shadow-sm"
          data-testid="add-transaction-button"
        >
          <Plus weight="bold" className="w-4 h-4" />
          Transaktion hinzufügen
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 p-6" data-testid="total-income-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center">
              <TrendUp weight="bold" className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-xs tracking-widest uppercase font-bold text-slate-500">Einnahmen</span>
          </div>
          <p className="font-mono text-3xl font-black tracking-tighter text-emerald-600">
            {formatCurrency(totals.income)}
          </p>
        </div>
        <div className="bg-white border border-gray-200 p-6" data-testid="total-expense-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 flex items-center justify-center">
              <TrendDown weight="bold" className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-xs tracking-widest uppercase font-bold text-slate-500">Ausgaben</span>
          </div>
          <p className="font-mono text-3xl font-black tracking-tighter text-red-600">
            {formatCurrency(totals.expense)}
          </p>
        </div>
        <div className="bg-white border border-gray-200 p-6" data-testid="net-total-card">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 flex items-center justify-center ${totals.income - totals.expense >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <CurrencyEur weight="bold" className={`w-5 h-5 ${totals.income - totals.expense >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
            </div>
            <span className="text-xs tracking-widest uppercase font-bold text-slate-500">Netto</span>
          </div>
          <p className={`font-mono text-3xl font-black tracking-tighter ${totals.income - totals.expense >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatCurrency(totals.income - totals.expense)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-4 flex items-center flex-wrap gap-4">
        <FunnelSimple weight="bold" className="w-5 h-5 text-slate-400" />
        <select
          value={filter.type}
          onChange={(e) => setFilter(prev => ({ ...prev, type: e.target.value, category: '' }))}
          className="border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none text-sm"
          data-testid="filter-type-select"
        >
          <option value="">Alle Typen</option>
          <option value="income">Einnahmen</option>
          <option value="expense">Ausgaben</option>
        </select>
        <select
          value={filter.category}
          onChange={(e) => setFilter(prev => ({ ...prev, category: e.target.value }))}
          className="border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none text-sm min-w-[140px]"
          data-testid="filter-category-select"
        >
          <option value="">Alle Arten</option>
          {filteredCategoryOptions.map(cat => (
            <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
          ))}
        </select>
        <select
          value={filter.property_id}
          onChange={(e) => setFilter(prev => ({ ...prev, property_id: e.target.value }))}
          className="border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none text-sm"
          data-testid="filter-property-select"
        >
          <option value="">Alle Immobilien</option>
          {properties.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select
          value={filter.month}
          onChange={(e) => setFilter(prev => ({ ...prev, month: parseInt(e.target.value) }))}
          className="border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none text-sm"
          data-testid="filter-month-select"
        >
          <option value={0}>Alle Monate</option>
          {MONTHS_DE.map((m, i) => (
            <option key={i + 1} value={i + 1}>{m}</option>
          ))}
        </select>
        <select
          value={filter.year}
          onChange={(e) => setFilter(prev => ({ ...prev, year: parseInt(e.target.value) }))}
          className="border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none text-sm"
          data-testid="filter-year-select"
        >
          <option value={0}>Alle Jahre</option>
          {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {(filter.type || filter.property_id || filter.category || filter.year || filter.month) && (
          <button
            onClick={() => setFilter({ type: '', property_id: '', category: '', year: 0, month: 0 })}
            className="text-xs text-slate-500 hover:text-red-600 underline ml-auto"
            data-testid="filter-clear"
          >
            Filter zurücksetzen
          </button>
        )}
      </div>

      {/* Transactions List */}
      {transactions.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <CurrencyEur weight="light" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Keine Transaktionen</h3>
          <p className="text-slate-500 mb-6">Fügen Sie Ihre erste Transaktion hinzu.</p>
          <button
            onClick={() => setShowModal(true)}
            className="bg-slate-900 text-white px-6 py-2.5 font-medium hover:bg-slate-800 inline-flex items-center gap-2"
          >
            <Plus weight="bold" className="w-4 h-4" />
            Erste Transaktion hinzufügen
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]" data-testid="transactions-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Typ</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Datum</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Kategorie</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Beschreibung</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Immobilie</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Betrag</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50 transition-colors" data-testid={`transaction-row-${transaction.id}`}>
                  <td className="py-4 px-4">
                    <div className={`w-8 h-8 flex items-center justify-center ${transaction.type === 'income' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                      {transaction.type === 'income' ? (
                        <TrendUp weight="bold" className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <TrendDown weight="bold" className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-sm text-slate-900">{transaction.date}</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium uppercase tracking-wider">
                      {getCategoryLabel(transaction.category)}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm text-slate-500">{transaction.description || '—'}</td>
                  <td className="py-4 px-4 text-sm text-slate-900">{getPropertyName(transaction.property_id)}</td>
                  <td className="py-4 px-4 text-right">
                    <span className={`font-mono font-medium ${transaction.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(transaction.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        data-testid={`delete-transaction-${transaction.id}`}
                        title="Löschen"
                      >
                        <Trash weight="bold" className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setEditingTx(transaction)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        data-testid={`edit-transaction-${transaction.id}`}
                        title="Bearbeiten"
                      >
                        <PencilSimple weight="bold" className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add Modal */}
      <TransactionModal
        open={showModal}
        onClose={() => setShowModal(false)}
        properties={properties}
        units={units}
        onSave={() => { loadData(); setShowModal(false); }}
      />

      {/* Edit Modal */}
      <TransactionModal
        open={!!editingTx}
        existing={editingTx}
        onClose={() => setEditingTx(null)}
        properties={properties}
        units={units}
        onSave={() => { loadData(); setEditingTx(null); }}
      />
    </div>
  );
}

function TransactionModal({ open, existing, onClose, properties, units, onSave }) {
  const isEdit = !!existing;
  const [formData, setFormData] = useState({
    type: 'expense',
    category: 'other',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    property_id: '',
    unit_id: ''
  });
  const [loading, setLoading] = useState(false);

  // When opening with an existing tx, prefill the form
  useEffect(() => {
    if (open && existing) {
      setFormData({
        type: existing.type || 'expense',
        category: existing.category || 'other',
        amount: existing.amount != null ? String(existing.amount) : '',
        date: existing.date || new Date().toISOString().split('T')[0],
        description: existing.description || '',
        property_id: existing.property_id || '',
        unit_id: existing.unit_id || ''
      });
    } else if (open && !existing) {
      setFormData({
        type: 'expense',
        category: 'other',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        property_id: '',
        unit_id: ''
      });
    }
  }, [open, existing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(String(formData.amount).replace(',', '.')),
        property_id: formData.property_id || null,
        unit_id: formData.unit_id || null
      };
      if (isEdit) {
        await updateTransaction(existing.id, payload);
      } else {
        await createTransaction(payload);
      }
      onSave();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUnits = formData.property_id 
    ? units.filter(u => u.property_id === formData.property_id)
    : [];

  const incomeCategories = ['rent', 'deposit', 'other'];
  const expenseCategories = ['utility', 'maintenance', 'repair', 'insurance', 'tax', 'management', 'other'];
  const categories = formData.type === 'income' ? incomeCategories : expenseCategories;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="transaction-modal">
      <div className="bg-white w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-heading text-xl font-bold text-slate-900">{isEdit ? 'Transaktion bearbeiten' : 'Neue Transaktion'}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Type Selection */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, type: 'income', category: 'rent' }))}
              className={`flex-1 py-3 font-medium flex items-center justify-center gap-2 transition-colors ${
                formData.type === 'income' 
                  ? 'bg-emerald-100 text-emerald-800 border-2 border-emerald-500' 
                  : 'bg-gray-100 text-slate-600 border-2 border-transparent'
              }`}
              data-testid="type-income-button"
            >
              <TrendUp weight="bold" className="w-5 h-5" />
              Einnahme
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, type: 'expense', category: 'other' }))}
              className={`flex-1 py-3 font-medium flex items-center justify-center gap-2 transition-colors ${
                formData.type === 'expense' 
                  ? 'bg-red-100 text-red-800 border-2 border-red-500' 
                  : 'bg-gray-100 text-slate-600 border-2 border-transparent'
              }`}
              data-testid="type-expense-button"
            >
              <TrendDown weight="bold" className="w-5 h-5" />
              Ausgabe
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Betrag (€)</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none font-mono text-xl"
                placeholder="0.00"
                required
                data-testid="transaction-amount-input"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Datum</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                required
                data-testid="transaction-date-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Kategorie</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              data-testid="transaction-category-select"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'rent' && 'Miete'}
                  {cat === 'deposit' && 'Kaution'}
                  {cat === 'utility' && 'Nebenkosten'}
                  {cat === 'maintenance' && 'Wartung'}
                  {cat === 'repair' && 'Reparatur'}
                  {cat === 'insurance' && 'Versicherung'}
                  {cat === 'tax' && 'Steuern'}
                  {cat === 'management' && 'Verwaltung'}
                  {cat === 'other' && 'Sonstige'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Beschreibung</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              placeholder="Optionale Beschreibung"
              data-testid="transaction-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Immobilie</label>
              <select
                value={formData.property_id}
                onChange={(e) => setFormData(prev => ({ ...prev, property_id: e.target.value, unit_id: '' }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                data-testid="transaction-property-select"
              >
                <option value="">Keine Zuordnung</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Einheit</label>
              <select
                value={formData.unit_id}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_id: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                disabled={!formData.property_id}
                data-testid="transaction-unit-select"
              >
                <option value="">Keine Einheit</option>
                {filteredUnits.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-2.5 font-medium disabled:opacity-50 ${
                formData.type === 'income' 
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700' 
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
              data-testid="transaction-save-button"
            >
              {loading ? 'Wird gespeichert...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
